// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu / Product page  (shared by components/menu/PackSheet and pages/ProductPage)
// The editable DRAFT of one catch's order: how many packs of each size and which cut, seeded from what the cart already
// holds (or one 500 g pack + the default cut for a new add) whenever a different catch is opened. Nothing is written to
// the cart here — the screen applies the draft on "Confirm / Add to cart" (see hooks/usePackSheet, ProductPage).
// Also derives the price of one pack with the chosen cut's prep fee, the draft total and a readable summary
// ("1 × 500 g + 1 × 1 kg"), so both screens always agree on the numbers.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { cutOf, prepFee, type CutId } from "../data/cuts";
import { PACK_SIZES, packPrice, type PackId, type PackSize } from "../data/packs";
import type { SeafoodItem } from "../data/seafood";

export const MAX_PACKS = 10;

export type Draft = Record<PackId, number>;

/** `maxTotal` caps the packs of ALL sizes together (a Daily Catch landing's packs left); omit for no cap. */
export default function useCatchDraft(item: SeafoodItem | null, inCart: Draft, cutInCart?: string, maxTotal?: number) {
  const alreadyInCart = PACK_SIZES.some((pack) => inCart[pack.id] > 0);
  const [draft, setDraft] = useState<Draft>({ "500g": 0, "1kg": 0 });
  const [cutId, setCutId] = useState<CutId | null>(null);

  // Seed when a different catch is opened. Later cart changes must not overwrite what the customer is editing.
  useEffect(() => {
    if (!item) return;
    setDraft(alreadyInCart ? { ...inCart } : { "500g": 1, "1kg": 0 });
    setCutId(cutOf(item, alreadyInCart ? cutInCart : undefined).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const cut = item ? cutOf(item, cutId ?? undefined) : null;

  /** Price of ONE pack of this size with the chosen cut's prep fee. */
  const unit = (pack: PackSize) => (item && cut ? packPrice(item, pack) + prepFee(cut, pack) : 0);

  const total = useMemo(
    () => (item && cut ? PACK_SIZES.reduce((sum, pack) => sum + draft[pack.id] * (packPrice(item, pack) + prepFee(cut, pack)), 0) : 0),
    [draft, item, cut],
  );
  const chosen = PACK_SIZES.filter((pack) => draft[pack.id] > 0);
  const packCount = PACK_SIZES.reduce((sum, pack) => sum + draft[pack.id], 0);

  return {
    draft,
    cutId,
    setCutId,
    cut,
    alreadyInCart,
    unit,
    total,
    chosen,
    summary: chosen.map((pack) => `${draft[pack.id]} × ${pack.label}`).join(" + "),
    empty: chosen.length === 0,
    /** True when no more packs may be added (only with `maxTotal`). */
    atLimit: maxTotal !== undefined && packCount >= maxTotal,
    set: (id: PackId, qty: number) =>
      setDraft((current) => {
        const others = PACK_SIZES.reduce((sum, pack) => (pack.id === id ? sum : sum + current[pack.id]), 0);
        const cap = maxTotal === undefined ? MAX_PACKS : Math.min(MAX_PACKS, Math.max(0, maxTotal - others));
        return { ...current, [id]: Math.max(0, Math.min(cap, qty)) };
      }),
  };
}
